import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    FormControl,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DateValidators } from '../../../shared/validators/date.validator';
import { Subscription } from 'rxjs';
import { OrgTreeActionsDirective } from '../directives/org-tree-actions.directive';
import { OrgTreeService } from '../services/org-tree.service';
import { DialogService } from '../../../shared/services/dialog.service';
import { FaqService } from '../../../shared/services/faq.service';
import { GuardService } from '../../../shared/services/guard.service';
import { SnackService } from '../../../shared/services/snack.service';
import { OrganisationConfigurationService } from '../../../shared/services/organisation-configuration.service';
import { IOrganisationConfiguration } from '../../../shared/interfaces/responsible/IOrganisationConfiguration';
import {
    beginningDate,
    currentDate,
    disableBackgroundScrolling,
    enableBackgroundScrolling
} from '../../../shared/helpers/helpers';
import { ITemplateInformation } from '../../../shared/interfaces/template/ITemplateInformation';
import { TemplateEditorComponent } from '../../template-editor/template-editor.component';
import { IReportTemplate } from '@shared/interfaces';
import moment from 'moment';
import { Moment } from 'moment';
import { ModuleService } from '../../../shared/services/module.service';
import { TranslateService } from '@ngx-translate/core';
import { IFaq } from '@shared/interfaces';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';
import { TemplateEditorPreviewComponent } from '@app/main/template-editor';

@Component({
    selector: 'app-org-settings',
    templateUrl: './org-settings.component.html',
    styleUrls: ['./org-settings.component.scss'],
    standalone: false
})
export class OrgSettingsComponent extends OrgTreeActionsDirective implements OnInit, OnDestroy {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    parentTemplate: ITemplateInformation;
    formGroup: UntypedFormGroup;
    subscriptions: Subscription[] = [];
    dontValidate = false;
    protected isLoading = false;

    constructor(
        private fb: UntypedFormBuilder,
        private settingsDialogRef: MatDialogRef<OrgSettingsComponent>,
        private snackService: SnackService,
        private organisationConfigurationService: OrganisationConfigurationService,
        private dialog: MatDialog,
        private guard: GuardService,
        private faqService: FaqService,
        private translate: TranslateService,
        private dialogService: DialogService,
        private snack: SnackService,
        orgTreeService: OrgTreeService,
        moduleService: ModuleService
    ) {
        super(orgTreeService, moduleService);
    }

    get dueDateFormControl(): UntypedFormControl {
        return this.formGroup.get('dueDate') as UntypedFormControl;
    }

    get dueDate(): Date {
        return new Date(this.dueDateFormControl.value);
    }

    get freezeDateFormControl(): UntypedFormControl {
        return this.formGroup.get('freezeDate') as UntypedFormControl;
    }

    get inheritedTemplateFromParentFormControl(): UntypedFormControl {
        return this.formGroup.get('inheritedTemplateFromParent') as UntypedFormControl;
    }

    set inheritedTemplateFromParent(value: boolean) {
        this.inheritedTemplateFromParentFormControl.patchValue(value);
    }

    get inheritedTemplateFromParent(): boolean {
        return this.inheritedTemplateFromParentFormControl.value;
    }

    get selectedTemplateFormControl(): UntypedFormControl {
        return this.formGroup.get('selectedTemplate') as UntypedFormControl;
    }

    get selectedTemplate(): ITemplateInformation {
        return this.selectedTemplateFormControl.value;
    }

    set selectedTemplate(value: IReportTemplate) {
        this.selectedTemplateFormControl.patchValue(
            value === null
                ? null
                : {
                      id: value.id,
                      title: value.title,
                      orgCode: value.orgCode,
                      responsibleType: value.responsibleType
                  }
        );
    }

    get settings(): IOrganisationConfiguration {
        return this.formGroup?.getRawValue();
    }

    get isEditable(): boolean {
        return (
            !!this.selectedTemplate && (this.guard.isAdmin || this.rootNode?.orgCode === this.selectedTemplate?.orgCode)
        );
    }

    ngOnInit(): void {
        disableBackgroundScrolling();
        this.buildForm();
    }

    ngOnDestroy() {
        enableBackgroundScrolling();
        this.subscriptions.forEach(e => e.unsubscribe());
    }

    openInfoModal() {
        let freezeDateFaqItem: IFaq | null;

        parentLoop: for (const faqItem of this.faqService.faqItems) {
            // loop over nested faq items when available
            for (const nestedFaqItem of faqItem.items || []) {
                // find the faq item describing Freeze date
                if (
                    nestedFaqItem.title[`${this.translate.currentLang}`] === 'Release date for the report form' ||
                    nestedFaqItem.title[`${this.translate.currentLang}`] === 'Freigabedatum des Berichtsformulars'
                ) {
                    freezeDateFaqItem = nestedFaqItem;
                    break parentLoop;
                }
            }
        }

        // no freeze date found => show error
        if (!freezeDateFaqItem) {
            this.snack.openSnackBarError(this.translate.instant('faq.noFreezeFoundError'));
            return;
        }

        const title = freezeDateFaqItem?.title[`${this.translate.currentLang}`];
        const content = freezeDateFaqItem?.content[`${this.translate.currentLang}`];

        this.dialogService.openDialog({ title: title, message: content, confirmText: 'Close', hideCancelButton: true });
    }

    async saveConfiguration(closeDialog = true) {
        if (!this.formGroup.valid) {
            this.snackService.openSnackBarError(`Please check your inputs.`);
            return;
        }

        try {
            this.currentConfiguration = await this.organisationConfigurationService.updateItem(this.formGroup.value);

            if (closeDialog) {
                this.settingsDialogRef.close();
            }
        } catch (e) {
            this.snackService.openSnackBarError(e.message);
        }
    }

    dueDateFilter = (d: Moment | null): boolean => {
        return (
            d?.isValid() &&
            (!this.currentConfiguration?.maxDueDate ||
                d.unix() <= moment(this.currentConfiguration?.maxDueDate)?.unix()) &&
            d.unix() > moment(beginningDate).unix()
        );
    };

    freezeDateFilter = (d: Moment | null): boolean => {
        if (IS_TRS_PROJECT) {
            return (
                d?.isValid() &&
                d.unix() >= moment(beginningDate.copyAndAddDays(-7)).unix() &&
                (!this.dueDate || d.unix() <= moment(this.dueDate)?.unix()) &&
                d.unix() > moment(currentDate.copyAndAddDays(1).atMidnight()).unix()
            );
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            return (
                d?.isValid() &&
                d.unix() >= moment(beginningDate.copyAndAddDays(-7)).unix() &&
                (!this.dueDate || d.unix() <= moment(this.dueDate)?.unix()) &&
                d.unix() > moment(currentDate.copyAndAddDays(0).atMidnight()).unix()
            );
        }
    };

    openTemplateEditor(item?: IReportTemplate) {
        if (IS_TRS_PROJECT) {
            this.dialog
                .open(TemplateEditorComponent, {
                    panelClass: 'template-dialog',
                    width: '100vw',
                    data: {
                        mode: item ? 'edit' : this.isOrgNode ? 'duplicate' : 'create',
                        id: item ? item.id : this.isOrgNode ? this.parentTemplate?.id : null,
                        organisation: this.rootNode
                    }
                })
                .beforeClosed()
                .subscribe(async e => {
                    if (e) {
                        this.selectedTemplate = e;
                        this.currentConfiguration = await this.organisationConfigurationService.updateItem(
                            this.formGroup.getRawValue()
                        );
                    }
                });
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            this.dialog
                .open(TemplateEditorComponent, {
                    panelClass: 'template-dialog',
                    width: '100vw',
                    data: {
                        mode: item
                            ? 'edit'
                            : this.isOrgNode || this.currentConfiguration?.selectedTemplate?.id
                              ? 'duplicate'
                              : 'create',
                        id: item
                            ? item.id
                            : this.isOrgNode || this.currentConfiguration?.selectedTemplate?.id
                              ? this.parentTemplate?.id || this.currentConfiguration?.selectedTemplate?.id
                              : null,
                        organisation: this.rootNode
                    }
                })
                .beforeClosed()
                .subscribe(async e => {
                    if (e) {
                        this.selectedTemplate = e;
                        this.currentConfiguration = await this.organisationConfigurationService.updateItem(
                            this.formGroup.getRawValue()
                        );
                    }
                });
        }
    }

    preview(template: IReportTemplate) {
        this.dialog.open(TemplateEditorPreviewComponent, {
            panelClass: 'custom-dialog',
            width: `${(window.innerWidth * 2) / 3}px`,
            data: template
        });
    }

    protected async inheritDates(): Promise<void> {
        this.isLoading = true;
        try {
            await this.organisationConfigurationService.inheritDates(this.currentConfiguration?.id);
        } catch (error) {
            this.isLoading = false;
            this.snack.openSnackBarError('An error occurred while inheriting dates');
            console.error('Error inheriting dates:', error);
        }
        this.isLoading = false;
    }

    private buildForm(): void {
        this.parentTemplate = this.currentConfiguration?.parentTemplate;

        this.formGroup = new FormGroup({
            id: new FormControl({ value: this.currentConfiguration?.id, disabled: false }),
            organisationId: new FormControl({ value: this.currentConfiguration?.id ?? this.getId(), disabled: false }),
            disableCascadingForChildren: new FormControl({
                value: this.currentConfiguration?.disableCascadingForChildren ?? false,
                disabled: false
            }),
            orgCode: new FormControl({ value: this.rootNode?.orgCode, disabled: false }),
            parentId: new FormControl({ value: this.rootNode?.parentId ?? 'root', disabled: false }),
            reportStartDate: new FormControl(
                {
                    value: this.currentConfiguration?.reportStartDate
                        ? new Date(this.currentConfiguration.reportStartDate)
                        : new Date(currentDate.getFullYear() - 1, 9, 1),
                    disabled: this.freezeDateReached
                },
                [Validators.required, DateValidators.dateLessThan('reportEndDate')]
            ),
            reportEndDate: new FormControl(
                {
                    value: this.currentConfiguration?.reportEndDate
                        ? new Date(this.currentConfiguration.reportEndDate)
                        : new Date(currentDate.getFullYear(), 8, 30),
                    disabled: this.freezeDateReached
                },
                [Validators.required]
            ),
            selectedTemplate: new FormControl({
                value: this.currentConfiguration?.selectedTemplate ?? null,
                disabled: false
            }),
            inheritedTemplateFromParent: new FormControl({
                value: this.currentConfiguration?.inheritedTemplateFromParent ?? this.isOrgNode,
                disabled: this.freezeDateReached
            }),
            dueDate: new FormControl(
                {
                    value: this.currentConfiguration?.dueDate
                        ? new Date(this.currentConfiguration.dueDate)
                        : new Date(currentDate.getFullYear(), 9, 31),
                    disabled: this.freezeDateReached
                },
                [Validators.required]
            ),
            freezeDate: new FormControl(
                {
                    value: this.currentConfiguration?.freezeDate
                        ? new Date(this.currentConfiguration.freezeDate)
                        : new Date(currentDate.getFullYear(), 6, 31),
                    disabled: this.freezeDateReached
                },
                [Validators.required, DateValidators.dateLessThan('dueDate')]
            )
        });

        this.subscriptions.push(
            this.dueDateFormControl.valueChanges.subscribe(() => {
                this.freezeDateFormControl.markAsTouched();
                this.freezeDateFormControl.updateValueAndValidity();
            }),
            this.inheritedTemplateFromParentFormControl.valueChanges.subscribe(
                e => !this.dontValidate && (this.selectedTemplate = e ? this.parentTemplate : null)
            ),
            this.selectedTemplateFormControl.valueChanges.subscribe((e: ITemplateInformation) => {
                this.dontValidate = true;

                if ((!e || (e && e.id !== this.parentTemplate?.id)) && this.inheritedTemplateFromParent) {
                    this.inheritedTemplateFromParent = false;
                }

                this.dontValidate = false;
            })
        );
    }

    private getId(): string {
        return this.rootNode?.id
            ? this.rootNode.id
            : this.rootNode?.responsibleType
              ? `responsibility-${this.rootNode?.responsibleType}`
              : null;
    }

    protected onDueDateChange(event: string): void {
        this.formGroup.get('dueDate')?.setValue(new Date(event));
        this.formGroup.get('dueDate')?.updateValueAndValidity();
    }

    protected onFreezeDateChange(event: string): void {
        this.formGroup.get('freezeDate')?.setValue(new Date(event));
        this.formGroup.get('freezeDate')?.updateValueAndValidity();
    }
}
